import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { guomanComics, rimanComics, allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function parseViews(v: string): number {
  if (v.includes('亿')) return parseFloat(v) * 10000;
  if (v.includes('万')) return parseFloat(v);
  return 0;
}

function RankRow({ comic, index }: { comic: Comic; index: number }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-primary-300 hover:shadow-soft"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-lg ${
          index === 0 ? 'bg-primary-500 text-background-50' : index === 1 ? 'bg-accent-500 text-background-50' : index === 2 ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-500'
        }`}
      >
        {index + 1}
      </span>
      <img src={comic.cover} alt={`${comic.title}榜单封面`} className="h-16 w-12 shrink-0 rounded-lg object-cover object-top" />
      <div className="min-w-0 flex-1">
        <h4 className="truncate font-medium text-foreground-950 group-hover:text-primary-600 transition-colors">{comic.title}</h4>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {comic.genres.slice(0, 2).map((g) => (
            <span key={g} className="rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700">
              {g}
            </span>
          ))}
        </div>
      </div>
      <div className="hidden flex-col items-end gap-1 text-right sm:flex">
        <span className="flex items-center gap-1 text-sm font-medium text-accent-600">
          <i className="ri-star-fill text-accent-500"></i>
          {comic.rating.toFixed(1)}
        </span>
        <span className="text-xs text-foreground-500">{comic.views} 人气</span>
      </div>
    </Link>
  );
}

const topRating = [...allComics].sort((a, b) => b.rating - a.rating).slice(0, 6);
const guomanTop = [...guomanComics].sort((a, b) => parseViews(b.views) - parseViews(a.views)).slice(0, 8);
const rimanTop = [...rimanComics].sort((a, b) => parseViews(b.views) - parseViews(a.views)).slice(0, 8);

const structured = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://oiutc07.cn/' },
        { '@type': 'ListItem', position: 2, name: '人气榜单', item: 'https://oiutc07.cn/top' },
      ],
    },
    {
      '@type': 'CollectionPage',
      name: '人气榜单 - 虫虫漫画',
      url: 'https://oiutc07.cn/top',
      mainEntity: {
        '@type': 'ItemList',
        name: '漫画人气榜单',
        itemListElement: topRating.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.title, url: `https://oiutc07.cn/comic/${c.id}` })),
      },
    },
  ],
};

export default function Top() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }} />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <nav className="text-sm text-foreground-500" aria-label="面包屑">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground-700" aria-current="page">人气榜单</li>
          </ol>
        </nav>

        <div className="mt-5">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
            人气<span className="text-primary-500">榜单</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">
            汇总国漫与日漫的人气排名与高分作品，帮你快速锁定值得入坑的口碑之作。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section>
            <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
              <i className="ri-fire-line text-primary-500"></i>国漫人气榜
            </h2>
            <div className="mt-4 space-y-3">
              {guomanTop.map((c, i) => (
                <RankRow key={c.id} comic={c} index={i} />
              ))}
            </div>
          </section>
          <section>
            <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
              <i className="ri-sparkling-line text-accent-500"></i>日漫人气榜
            </h2>
            <div className="mt-4 space-y-3">
              {rimanTop.map((c, i) => (
                <RankRow key={c.id} comic={c} index={i} />
              ))}
            </div>
          </section>
        </div>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
            <i className="ri-star-fill text-secondary-500"></i>高分口碑总榜
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {topRating.map((c) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex gap-4 rounded-2xl border border-background-200 bg-background-100 p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <div className="relative h-36 w-24 shrink-0 overflow-hidden rounded-lg">
                  <img src={c.cover} alt={`${c.title}高分封面`} className="h-full w-full object-cover object-top" />
                  <span className="absolute left-1.5 top-1.5 rounded-md bg-secondary-500 px-1.5 py-0.5 text-xs text-background-50">
                    {c.rating.toFixed(1)}
                  </span>
                </div>
                <div className="flex min-w-0 flex-col">
                  <h3 className="truncate font-medium text-foreground-950 group-hover:text-secondary-600 transition-colors">{c.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {c.genres.slice(0, 3).map((g) => (
                      <span key={g} className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                        {g}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">{c.intro}</p>
                  <span className="mt-auto pt-2 text-xs text-foreground-500">
                    {c.status} · {c.chapters}话
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}